import GoogleIcon from "@mui/icons-material/Google";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { useAuth } from "@bw/shared/hooks";
import { sanitizeNext } from "@bw/shared/sanitize-next";

const loginFormSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get("next")) ?? "/workspaces";
  const { authError, signIn, signInWithEmail } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: "", password: "" },
  });

  const run = async (action: () => Promise<void> | undefined) => {
    setError(null);
    setSubmitting(true);
    try {
      await action();
      navigate(next, { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (values: LoginFormValues) =>
    run(() => signInWithEmail?.(values.email, values.password));

  const beginGoogleSignIn = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await signIn();
      // Google redirects away from this document. When the browser returns,
      // SignInPage's existing useAuthedRedirect owns the safe `?next=` landing.
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Paper
        component="form"
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        variant="outlined"
        sx={{ p: { xs: 2.5, sm: 3 } }}
      >
        <Stack spacing={2.25}>
          <Typography color="text.secondary" variant="body2">
            Use the same account you use to play, coach, or manage your organization.
          </Typography>
          {error ?? authError ? (
            <Alert severity="error" role="alert">
              {error ?? authError}
            </Alert>
          ) : null}
          <TextField
            autoComplete="email"
            error={Boolean(form.formState.errors.email)}
            helperText={form.formState.errors.email?.message}
            label="Email"
            placeholder="you@example.com"
            type="email"
            {...form.register("email")}
          />
          <Stack spacing={0.75}>
            <TextField
              autoComplete="current-password"
              error={Boolean(form.formState.errors.password)}
              helperText={form.formState.errors.password?.message}
              label="Password"
              type="password"
              {...form.register("password")}
            />
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{ alignSelf: "flex-end" }}
            >
              Forgot your password?
            </Link>
          </Stack>
          <Button
            disabled={submitting}
            fullWidth
            startIcon={
              submitting ? (
                <CircularProgress
                  aria-hidden="true"
                  color="inherit"
                  size={16}
                />
              ) : undefined
            }
            type="submit"
            variant="contained"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
          <Divider>or</Divider>
          <Button
            disabled={submitting}
            fullWidth
            onClick={() => void beginGoogleSignIn()}
            startIcon={<GoogleIcon />}
            type="button"
            variant="outlined"
          >
            Sign in with Google
          </Button>
          <Stack
            spacing={1.25}
            sx={{ alignItems: "center", textAlign: "center" }}
          >
            <Typography variant="body2">
              Don&apos;t have an account?{" "}
              <Link component={RouterLink} to={`/sign-up?next=${encodeURIComponent(next)}`}>
                Sign up
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{ textAlign: "center" }}
      >
        By continuing, you agree to our{" "}
        <Link component={RouterLink} to="/terms">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link component={RouterLink} to="/privacy">
          Privacy Policy
        </Link>
        .
      </Typography>
    </Stack>
  );
}
